import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import IdChip from "@/components/IdChip";

const FIELDS = [
  { key: "club_name", label: "Club Name", placeholder: "My Crest Club" },
  { key: "established_year", label: "Established Year", placeholder: "2026" },
  { key: "address", label: "Address", placeholder: "123 Fairway Lane" },
  { key: "city", label: "City / Region", placeholder: "Pebble Beach, CA" },
  { key: "phone", label: "Phone", placeholder: "(555) 123-4567" },
  { key: "contact_email", label: "Contact Email", placeholder: "theshop@mycrestclub.com" },
  { key: "website", label: "Website", placeholder: "https://mycrestclub.com" },
];

export default function ClubProfilePage() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    api
      .get("/admin/club-profile")
      .then((r) => setForm(r.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await api.patch("/admin/club-profile", form);
      setForm(data || {});
      setFeedback({ type: "success", text: "Club profile saved." });
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-12">
        <div className="overline">Pro Shop · Settings</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
          Club Profile
        </h1>
        <p className="text-[#4A4A4A] mt-3 max-w-2xl">
          Everything the pro shop publishes about the club. Used on member-facing
          surfaces and the footer of every outgoing email.
        </p>

        {feedback && (
          <div
            className={`mt-6 px-4 py-3 text-sm border ${
              feedback.type === "success"
                ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
            }`}
          >
            {feedback.text}
          </div>
        )}

        {loading ? (
          <div className="mt-8 overline text-[#7A7A7A]">Loading…</div>
        ) : (
          <div className="mt-8 surface-card p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <React.Fragment key={f.key}>
                {f.key === "contact_email" && form.club_id && (
                  <div className="sm:col-span-2" data-testid="club-profile-club-id-row">
                    <label className="label-elegant block mb-2">Club ID</label>
                    <div className="flex items-center gap-3">
                      <IdChip
                        value={form.club_id}
                        label="Club"
                        testId="club-profile-club-id"
                      />
                      <span className="text-[11px] text-[#7A7A7A]">
                        Read-only · share this with support when you open a ticket.
                      </span>
                    </div>
                  </div>
                )}
                <div className={f.key === "address" ? "sm:col-span-2" : ""}>
                  <label className="label-elegant block mb-2">{f.label}</label>
                  <input
                    type="text"
                    value={form[f.key] || ""}
                    onChange={update(f.key)}
                    data-testid={`club-${f.key}`}
                    className="input-field"
                    placeholder={f.placeholder}
                  />
                </div>
              </React.Fragment>
            ))}
            <div className="sm:col-span-2">
              <label className="label-elegant block mb-2">Internal Notes</label>
              <textarea
                rows={4}
                value={form.notes || ""}
                onChange={update("notes")}
                data-testid="club-notes"
                className="input-field"
                placeholder="Anything the team should keep in mind."
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="club-save"
                className="btn-primary"
              >
                {saving ? "Saving…" : "Save Club Profile"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
